import { Button, Row, Segmented, Skeleton, theme } from 'antd';
import { useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CreateTicketFormValues } from '../../../../../../../types/form';
import TicketHistoryList from '../../../../../../components/TicketHistoryList';
import { isChannelGSocial, isChannelGSocialChat } from '../../../../../../components/TicketPage/CommentTypeSelect';
import { ConversationList } from '../../../../../../components/TicketPage/Conversations';
import GSocialChatContainer from '../../../../../../components/TicketPage/GSocialChat/GSocialChatContainer';
import TicketMergeModal, { TicketMergeModalRef } from '../../../../../../components/TicketPage/TicketMergeModal';
import AgentChatContainer from '../../../../../../components/plugins/chat/AgentChatContainer';
import CallTicketAssociatonContainer from '../../../../../../containers/Pages/TicketPages/TicketPagesContentLayout/TicketDetailContentLayout/CallTicketAssociationContainer';
import useCurrentTicket from '../../../../../../hooks/ticketDetailPage/useCurrentTicket';
import useCurrentTicketChannel from '../../../../../../hooks/ticketDetailPage/useCurrentTicketChannel';
import useCurrentTicketStatus from '../../../../../../hooks/ticketDetailPage/useCurrentTicketStatus';
import {
    DetailedUser,
    SetStateType,
    TicketSystemFieldsKey,
    channelType,
    channelTypeMap,
    statusOptions,
    ticketInteractionViewOptionsMap,
    ticketInteractionViewViewType
} from '../../../../../../model/types';

interface Props {
    commentsViewType: ticketInteractionViewViewType;
    setCommentsViewType: SetStateType<ticketInteractionViewViewType>;
}

export default function TicketInteractivityContainer({ commentsViewType, setCommentsViewType }: Props) {
    const {
        token: { colorBgLayout, paddingSM }
    } = theme.useToken();

    const { t } = useTranslation('tickets');

    const { data: ticketDetail } = useCurrentTicket();
    const ticketChannel = useCurrentTicketChannel();
    const ticketStatus = useCurrentTicketStatus();
    const { setValue } = useFormContext<CreateTicketFormValues>();

    const modalRef = useRef<TicketMergeModalRef>(null);

    if (!ticketDetail || !ticketChannel || !ticketStatus) return <Skeleton active className="full-width" />;

    const isConversationMode = commentsViewType === ticketInteractionViewOptionsMap.conversations;
    const isClosedTicket = ticketStatus === statusOptions.Closed;
    const isCallTicket = ticketChannel === channelTypeMap.CALL;
    const isChatTicket = ticketChannel === channelTypeMap.CHAT;
    const isGSocialChatTicket = isChannelGSocialChat(ticketChannel);

    const isCallMergeStatusNotDecided = isCallTicket && ticketDetail.callMergeStatus === 'TO_BE_DECIDED';
    const isCallTicketAssociationPanelVisible = isConversationMode && isCallMergeStatusNotDecided && !isClosedTicket;

    const isTicketAssociationModalButtonVisible = isConversationMode && !isCallMergeStatusNotDecided && !isClosedTicket;

    const isEventsMode = commentsViewType === ticketInteractionViewOptionsMap.events;
    const isChatMode = isChatTicket && commentsViewType === ticketInteractionViewOptionsMap.chat;
    const isGSocialChatMode = isGSocialChatTicket && commentsViewType === ticketInteractionViewOptionsMap.gsocialChat;

    const contentSegments = Object.keys(ticketInteractionViewOptionsMap)
        .filter(
            (view: ticketInteractionViewViewType) =>
                (isChatTicket || view !== ticketInteractionViewOptionsMap.chat) &&
                (isGSocialChatTicket || view !== ticketInteractionViewOptionsMap.gsocialChat)
        )
        .map((view: ticketInteractionViewViewType) => ({
            label:
                view === ticketInteractionViewOptionsMap.gsocialChat
                    ? t(`channelNames.${ticketChannel as channelType}`)
                    : t(view),
            value: view
        }));

    const handleOnChangeCommentsViewType = (value: ticketInteractionViewViewType) => {
        const isConversationMode = value === ticketInteractionViewOptionsMap.conversations;
        const isGSocialChatMode = isGSocialChatTicket && value === ticketInteractionViewOptionsMap.gsocialChat;

        if (isConversationMode || isGSocialChatMode) {
            const channel = isConversationMode ? channelTypeMap.WEB : ticketChannel;
            setValue('comment.channel', channel);
        }

        setCommentsViewType(value);
    };

    return (
        <>
            <Row
                justify="space-between"
                style={{ backgroundColor: colorBgLayout, padding: paddingSM, position: 'sticky', top: 0, zIndex: 1 }}
            >
                <Segmented
                    size="middle"
                    value={commentsViewType}
                    options={contentSegments}
                    onChange={handleOnChangeCommentsViewType}
                />

                {isTicketAssociationModalButtonVisible ? (
                    <Button type="link" onClick={() => modalRef.current.open()}>
                        {t('associate.description', { ticketKey: ticketDetail?.key })}
                    </Button>
                ) : null}
            </Row>

            {isCallTicketAssociationPanelVisible && (
                <CallTicketAssociatonContainer
                    ticketRequester={ticketDetail.fieldMap[TicketSystemFieldsKey.requester].value as DetailedUser}
                />
            )}
            {isTicketAssociationModalButtonVisible && <TicketMergeModal ticket={ticketDetail} ref={modalRef} />}

            {!commentsViewType && <Skeleton active className="full-width" />}

            {isConversationMode && (
                <ConversationList
                    comments={ticketDetail?.comments}
                    isGSocialIntegrated={isChannelGSocial(ticketChannel)}
                />
            )}
            {isGSocialChatTicket && (
                <GSocialChatContainer isVisible={isGSocialChatMode} comments={ticketDetail?.comments} />
            )}
            {isChatTicket && <AgentChatContainer isChildrenVisible={isChatMode} />}
            {isEventsMode && <TicketHistoryList />}
        </>
    );
}
